/* ========================================
   ANIMATION STATE MANAGER
   ======================================== */
const AnimationState = {
  isAnimating: false,
  expandedColumn: null,
  currentModalIndex: 0,
  
  setAnimating(value) {
    this.isAnimating = value;
  },
  
  canAnimate() {
    return !this.isAnimating;
  },
  
  setExpandedColumn(column) {
    this.expandedColumn = column;
  }
};

/* ========================================
   UTILITY FUNCTIONS
   ======================================== */
const Utils = {
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },
  
  isMobile() {
    return window.innerWidth < 750;
  }
};

/* ========================================
   MODAL UTILITIES
   ======================================== */
const ModalManager = {
  create(column, allItems) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    document.body.appendChild(overlay);
    
    const modal = document.createElement('div');
    modal.className = 'modal-content';

    const parentContainer = column.closest('.optm-model-column-container');
    if (parentContainer?.classList.contains('column-womens')) modal.classList.add('column-womens');
      
    const columnContent = column.cloneNode(true);
    const existingCloseBtn = columnContent.querySelector('.close-btn');
    if (existingCloseBtn) existingCloseBtn.remove();
    
    // Show text in modal (mobile view should always show text)
    const descriptionText = columnContent.querySelector('.optm-column-description p');
    if (descriptionText) {
      descriptionText.style.display = 'flex';
      descriptionText.style.opacity = '1';
      descriptionText.style.filter = 'none';
    }
    
    modal.appendChild(columnContent);
    document.body.appendChild(modal);
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'close-btn btn-circle modal-nav-btn';
    closeBtn.innerHTML = '<span>×</span>';
    closeBtn.setAttribute('aria-label', 'Close');
    document.body.appendChild(closeBtn);
    
    const prevBtn = document.createElement('button');
    prevBtn.className = 'modal-nav-btn modal-nav-prev btn-circle';
    prevBtn.innerHTML = '<span>◀</span>';
    prevBtn.setAttribute('aria-label', 'Previous item');
    document.body.appendChild(prevBtn);

    const nextBtn = document.createElement('button');
    nextBtn.className = 'modal-nav-btn modal-nav-next btn-circle';
    nextBtn.innerHTML = '<span>▶</span>';
    nextBtn.setAttribute('aria-label', 'Next item');
    document.body.appendChild(nextBtn);
    
    return { overlay, modal, closeBtn, prevBtn, nextBtn };
  },
  
  updateContent(modal, newColumn) {
    const existingCloseBtn = newColumn.querySelector('.close-btn');
    if (existingCloseBtn) existingCloseBtn.remove();
    
    // Show text in the new content
    const descriptionText = newColumn.querySelector('.optm-column-description p');
    if (descriptionText) {
      descriptionText.style.display = 'block';
      descriptionText.style.opacity = '1';
      descriptionText.style.filter = 'none';
    }
    
    gsap.to(modal, {
      opacity: 0,
      duration: 0.2,
      onComplete: () => {
        modal.innerHTML = '';
        modal.appendChild(newColumn);
        gsap.to(modal, { opacity: 1, duration: 0.2 });
      }
    });
  },
  
  open(elements) {
    const { overlay, modal, closeBtn, prevBtn, nextBtn } = elements;
    
    gsap.set([overlay, modal, closeBtn, prevBtn, nextBtn], { opacity: 0 });
    gsap.set(overlay, { display: 'block' });
    gsap.set(modal, { display: 'block' });
    
    const timeline = gsap.timeline({
      onComplete: () => AnimationState.setAnimating(false)
    });
    
    timeline.to([overlay, modal, closeBtn, prevBtn, nextBtn], {
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out'
    });
    
    return timeline;
  },
  
  close(elements, column) {
    const { overlay, modal, closeBtn, prevBtn, nextBtn } = elements;
    
    const timeline = gsap.timeline({
      onComplete: () => {
        overlay.remove();
        modal.remove();
        closeBtn.remove();
        prevBtn.remove();
        nextBtn.remove();
        column.classList.remove('expanded');
        AnimationState.setExpandedColumn(null);
        AnimationState.setAnimating(false);
      }
    });
    
    timeline.to([overlay, modal, closeBtn, prevBtn, nextBtn], {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in'
    });
    
    return timeline;
  }
};

/* ========================================
   COLUMN ANIMATION UTILITIES
   ======================================== */
const ColumnAnimations = {
  expandDesktop(column) {
    AnimationState.setAnimating(true);
    const descriptionText = column.querySelector('.optm-column-description p');
    const expandBtn = column.querySelector('.optm-column-expand-btn');
    
    const timeline = gsap.timeline({
      onComplete: () => AnimationState.setAnimating(false)
    });
    
    // If there's a previously expanded column, collapse it first
    if (AnimationState.expandedColumn && AnimationState.expandedColumn !== column) {
      const prevDescriptionText = AnimationState.expandedColumn.querySelector('.optm-column-description p');
      const prevExpandBtn = AnimationState.expandedColumn.querySelector('.optm-column-expand-btn');
      
      timeline.to(prevDescriptionText, {
        opacity: 0,
        filter: 'blur(10px)',
        duration: 0.2,
        ease: 'power2.in'
      })
      .set(prevDescriptionText, { display: 'none' })
      .to(AnimationState.expandedColumn, {
        width: 400,
        x: 0, // Reset position
        duration: 0.4,
        ease: 'power2.inOut'
      }, 0);
      
      if (prevExpandBtn) {
        prevExpandBtn.innerHTML = '<span>+</span>';
      }
      
      AnimationState.expandedColumn.classList.remove('expanded');
    }
    
    // Update expand button BEFORE animation
    if (expandBtn) {
      expandBtn.innerHTML = '<span>×</span>';
    }
    
    // Animate width expansion with center anchoring
    timeline.to(column, {
      width: 800,
      duration: 0.4,
      ease: 'power2.out',
      onStart: () => {
        column.classList.add('expanded');
      }
    })
    .set(descriptionText, { 
      display: 'block'
    }, '+=0.05')
    .fromTo(descriptionText, {
      opacity: 0,
      filter: 'blur(10px)'
    }, {
      opacity: 1,
      filter: 'blur(0px)',
      duration: 0.3,
      ease: 'power2.out'
    });
    
    AnimationState.setExpandedColumn(column);
    
    return timeline;
  },
  
  collapseDesktop(column) {
    AnimationState.setAnimating(true);
    const descriptionText = column.querySelector('.optm-column-description p');
    const expandBtn = column.querySelector('.optm-column-expand-btn');
    
    const timeline = gsap.timeline({
      onComplete: () => {
        column.classList.remove('expanded');
        AnimationState.setExpandedColumn(null);
        AnimationState.setAnimating(false);
      }
    });
    
    if (expandBtn) {
      expandBtn.innerHTML = '<span>+</span>';
    }
    
    if (descriptionText) {
      timeline.to(descriptionText, {
        opacity: 0,
        filter: 'blur(10px)',
        duration: 0.2,
        ease: 'power2.in'
      })
      .set(descriptionText, { display: 'none' });
    }
    
    timeline.to(column, {
      width: 400,
      x: 0, // Return to original position
      duration: 0.4,
      ease: 'power2.inOut'
    }, 0);
    
    return timeline;
  }
};

/* ========================================
   TAB NAVIGATION
   ======================================== */
const TabNavigation = {
  switchTab(currentContainer, targetContainer, targetIndex) {
    AnimationState.setAnimating(true);
    
    const timeline = gsap.timeline({
      onComplete: () => AnimationState.setAnimating(false)
    });
    
    timeline
      .to(currentContainer, {
        opacity: 0,
        filter: 'blur(20px)',
        duration: 0.3,
        ease: 'power2.in'
      })
      .set(currentContainer, { display: 'none' })
      .set(targetContainer, { display: 'flex', opacity: 0, y: 20 })
      .call(() => {
        if (!Utils.isMobile()) {
          const columnCount = targetContainer.querySelectorAll('.optm-model-column-item').length;
          targetContainer.style.setProperty('--column-count', columnCount);
        }
      })
      .to(targetContainer, {
        opacity: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    
    return timeline;
  }
};

/* ========================================
   FNB EXPAND/COLLAPSE
   ======================================== */
const FNBManager = {
  toggle(item, expandBtn) {
    if (AnimationState.isAnimating) return;
    
    AnimationState.setAnimating(true);
    const content = item.querySelector('.fnb-content');
    const copy = content.querySelector('.fnb-content-copy');
    const video = item.querySelector('.fnb-image');
    const isExpanded = item.classList.contains('expanded');
    
    const timeline = gsap.timeline({
      onComplete: () => AnimationState.setAnimating(false)
    });
    
    if (isExpanded) {
      item.classList.remove('expanded');
      expandBtn.innerHTML = '<span>+</span>';
      
      timeline
        .to(copy, {
          opacity: 0,
          duration: 0.1,
          ease: 'power2.in'
        })
        .set(copy, { display: 'none' })
        .to(video, {
          filter: 'blur(0px) brightness(1)',
          duration: 0.4,
          ease: 'power2.out'
        }, 0);
    } else {
      item.classList.add('expanded');
      expandBtn.innerHTML = '<span>×</span>';
      
      timeline
        .to(video, {
          filter: 'blur(8px) brightness(0.5)',
          duration: 0.4,
          ease: 'power2.out'
        })
        .set(copy, { display: 'block' })
        .to(copy, {
          opacity: 1,
          duration: 0.1,
          ease: 'power2.out',
          delay: 0.2
        });
    }
    
    return timeline;
  }
};

/* ========================================
   MAIN INITIALIZATION
   ======================================== */
document.addEventListener('DOMContentLoaded', function() {
  const navItems = document.querySelectorAll('.optm-model-nav-item');
  const containers = document.querySelectorAll('.optm-model-column-container');
  const columnItems = document.querySelectorAll('.optm-model-column-item');
  const leftArrow = document.querySelector('.optm-scroll-arrow-left');
  const rightArrow = document.querySelector('.optm-scroll-arrow-right');
  
  // Track the active container for scroll arrows
  let activeContainer = document.querySelector('.optm-model-column-container:first-of-type');
  const scrollAmount = 400 + 48;

  // Initialize GSAP ScrollTrigger
  gsap.registerPlugin(ScrollTrigger);
  
  // Optimize ScrollTrigger for mobile
  ScrollTrigger.config({
    limitCallbacks: true,
    syncInterval: Utils.isMobile() ? 150 : 0,
    ignoreMobileResize: true
  });
  
  // Blur to Focus scroll transitions - ONLY on desktop
  if (!Utils.isMobile()) {
    const sections = document.querySelectorAll('.optm-plp section');
    
    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      
      // Only animate sections that are below the fold
      if (rect.top > window.innerHeight) {
        gsap.from(section, {
          opacity: 0,
          filter: 'blur(20px)',
          duration: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            end: 'top 40%',
            scrub: 1,
            markers: false
          }
        });
      }
    });
    
    ScrollTrigger.refresh();
  }
  
  // FNB Video ScrollTrigger
  const fnbVideos = document.querySelectorAll('.fnb-video');
  fnbVideos.forEach((video) => {
    ScrollTrigger.create({
      trigger: video,
      start: 'top 80%',
      once: true,
      markers: false,
      onEnter: () => video.play()
    });
  }); 
  
  // Hide all containers except the first one initially
  containers.forEach((container, index) => {
    if (index !== 0) {
      gsap.set(container, { display: 'none', opacity: 0, y: 20 });
    } else {
      gsap.set(container, { display: 'flex', opacity: 1, y: 0 });
      if (!Utils.isMobile()) {
        const columnCount = container.querySelectorAll('.optm-model-column-item').length;
        container.style.setProperty('--column-count', columnCount);
      }
    }
  });
  
  /* ========================================
     SCROLL ARROW HANDLERS
     ======================================== */
  if (leftArrow && rightArrow) {
    leftArrow.addEventListener('click', () => {
      if (activeContainer) {
        activeContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    });

    rightArrow.addEventListener('click', () => {
      if (activeContainer) {
        activeContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    });
  }
  
  /* ========================================
     TAB NAVIGATION HANDLERS
     ======================================== */
  navItems.forEach((item, index) => {
    item.addEventListener('click', function() {
      if (!AnimationState.canAnimate()) return;
      if (this.classList.contains('active')) return;
      
      // Close any expanded columns first
      if (AnimationState.expandedColumn) {
        const overlay = document.querySelector('.modal-overlay');
        const modal = document.querySelector('.modal-content');
        const closeBtn = document.querySelector('.close-btn');
        const prevBtn = document.querySelector('.modal-nav-prev');
        const nextBtn = document.querySelector('.modal-nav-next');
        
        if (Utils.isMobile() && overlay && modal) {
          ModalManager.close(
            { overlay, modal, closeBtn, prevBtn, nextBtn },
            AnimationState.expandedColumn
          );
        } else if (!Utils.isMobile()) {
          ColumnAnimations.collapseDesktop(AnimationState.expandedColumn);
        }
      }
      
      navItems.forEach(nav => nav.classList.remove('active'));
      this.classList.add('active');
      
      const currentContainer = document.querySelector('.optm-model-column-container[style*="flex"]') || containers[0];
      const targetContainer = containers[index];
      
      // Update active container for scroll arrows
      activeContainer = targetContainer;
      
      TabNavigation.switchTab(currentContainer, targetContainer, index);
    });
  });
  
  /* ========================================
     COLUMN EXPANSION HANDLERS
     ======================================== */
  columnItems.forEach((column) => {
    // Add expand button to each column (desktop only)
    if (!Utils.isMobile()) {
      const expandBtn = document.createElement('button');
      expandBtn.className = 'optm-column-expand-btn btn-circle';
      expandBtn.innerHTML = '<span>+</span>';
      expandBtn.setAttribute('aria-label', 'Expand');
      column.appendChild(expandBtn);
      
      // Expand button click handler
      expandBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        if (!AnimationState.canAnimate()) return;
        
        if (column.classList.contains('expanded')) {
          ColumnAnimations.collapseDesktop(column);
        } else {
          ColumnAnimations.expandDesktop(column);
        }
      });
    }
    
    column.addEventListener('click', function(e) {
      if (e.target.closest('a, button, input, select, textarea')) return;
      if (!AnimationState.canAnimate()) return;
      
      if (Utils.isMobile()) {
        // Mobile: Open modal
        if (this.classList.contains('expanded')) return;
        
        AnimationState.setAnimating(true);
        this.classList.add('expanded');
        AnimationState.setExpandedColumn(this);
        
        const currentContainer = this.closest('.optm-model-column-container');
        const allItems = Array.from(currentContainer.querySelectorAll('.optm-model-column-item'));
        let currentIndex = allItems.indexOf(this);
        
        const modalElements = ModalManager.create(this, allItems);
        const { overlay, modal, closeBtn, prevBtn, nextBtn } = modalElements;
        
        // Previous button handler
        prevBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          if (currentIndex > 0) {
            currentIndex--;
            const newContent = allItems[currentIndex].cloneNode(true);
            ModalManager.updateContent(modal, newContent);
          }
        });

        // Next button handler
        nextBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          if (currentIndex < allItems.length - 1) {
            currentIndex++;
            const newContent = allItems[currentIndex].cloneNode(true);
            ModalManager.updateContent(modal, newContent);
          }
        });
        
        // Close modal function
        const closeModal = (e) => {
          if (e) e.stopPropagation();
          if (!AnimationState.canAnimate()) return;
          ModalManager.close(modalElements, column);
        };
        
        closeBtn.addEventListener('click', closeModal);
        overlay.addEventListener('click', closeModal);
        
        ModalManager.open(modalElements);
        
      } else {
        // Desktop: Expand/collapse column (same as button click)
        if (this.classList.contains('expanded')) {
          ColumnAnimations.collapseDesktop(this);
        } else {
          ColumnAnimations.expandDesktop(this);
        }
      }
    });
  });

  /* ========================================
     FNB EXPAND/COLLAPSE
     ======================================== */
  const fnbItems = document.querySelectorAll('.fnb-2, .fnb-3');
  
  fnbItems.forEach((item) => {
    const content = item.querySelector('.fnb-content');
    const copy = content.querySelector('.fnb-content-copy');
    gsap.set(copy, { opacity: 0, display: 'none' });
    
    const expandBtn = document.createElement('button');
    expandBtn.className = 'fnb-expand-btn btn-circle';
    expandBtn.innerHTML = '<span>+</span>';
    expandBtn.setAttribute('aria-label', 'Expand content');
    item.appendChild(expandBtn);
    
    expandBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      FNBManager.toggle(item, expandBtn);
    });
  });

  /* ========================================
      LOADER ANIMATION
      ======================================== */
  const pageLoader = document.querySelector('.optm-page-loader');
  if (pageLoader) {
    document.body.style.overflow = 'hidden';
    
    setTimeout(() => {
      gsap.to(pageLoader, {
        opacity: 0,
        filter: 'blur(10px)',
        duration: 0.25,
        ease: 'power2.out',
        onComplete: () => {
          pageLoader.remove();
          document.body.style.overflow = 'auto';
          
          // Start hero video after loader finishes
          const heroVideo = document.querySelector('.optm-hero-video');
          if (heroVideo) {
            heroVideo.play();
          }
        }
      });
    }, 2000);
  }
  
  /* ========================================
     HERO HEIGHT CALCULATION 
     ======================================== */
  (function() {
    function setHeroHeight() {
      const stickyBar = document.querySelector('.sticky-bar');
      const stickyBarHeight = stickyBar?.offsetHeight || 0;
      
      const header = document.querySelector('#shopify-section-header');
      const headerHeight = header?.offsetHeight || 0;
      
      const announcementBar = document.querySelector('.announcement-bar');
      const announcementBarHeight = announcementBar?.offsetHeight || 0;
      
      const plpHeader = document.querySelector('.optm-plp-header');
      const plpHeaderHeight = plpHeader?.offsetHeight || 0;

      // Calculate total header height
      const totalHeaderHeight = stickyBarHeight + headerHeight + announcementBarHeight + plpHeaderHeight;

      if (totalHeaderHeight > 0) {
        document.documentElement.style.setProperty('--total-header-height', `${totalHeaderHeight}px`);
      }
    }

    setTimeout(setHeroHeight, 100);
    window.addEventListener('load', () => setTimeout(setHeroHeight, 100));
    window.addEventListener('resize', setHeroHeight);
  })();

  /* ========================================
     HEADER STICKY & PROGRESS BAR
     ======================================== */
  const header = document.querySelector('.optm-plp-header');
  if (header) {
    // Get sticky bar height
    const stickyBar = document.querySelector('#header-group');
    const stickyBarHeight = stickyBar ? stickyBar.offsetHeight : 0;
    
    setTimeout(() => {
      initStickyHeader();
      initProgressBar();
    }, 2500);

    function initStickyHeader() {
      const optmPlp = document.querySelector('.optm-plp');
      
      // Store the initial header position ONCE at the start
      const headerInitialTop = header.offsetTop;
      
      ScrollTrigger.create({
        trigger: header,
        start: `top top`,
        end: '+=1',
        onEnter: () => {
          header.classList.add('sticky');
          header.style.top = `0`;
          if (optmPlp) {
            optmPlp.style.paddingTop = `${header.offsetHeight}px`;
          }
        },
        onLeaveBack: () => {
          header.classList.remove('sticky');
          header.style.top = '';
          if (optmPlp) {
            optmPlp.style.paddingTop = '';
          }
        },
        // Add onUpdate as fallback for fast scrolling
        onUpdate: (self) => {
          const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
          
          // Use the stored initial position instead of calculating it each time
          if (scrollPosition >= headerInitialTop) {
            if (!header.classList.contains('sticky')) {
              header.classList.add('sticky');
              header.style.top = `0`;
              if (optmPlp) {
                optmPlp.style.paddingTop = `${header.offsetHeight}px`;
              }
            }
          } else {
            // If scrolled back above header, remove sticky
            if (header.classList.contains('sticky')) {
              header.classList.remove('sticky');
              header.style.top = '';
              if (optmPlp) {
                optmPlp.style.paddingTop = '';
              }
            }
          }
        }
      });
    }

    function initProgressBar() {
      const progressBar = document.querySelector('.optm-progress-bar');
      if (progressBar) {
        // Use a custom calculation instead of ScrollTrigger for more stable mobile behavior
        if (Utils.isMobile()) {
          let ticking = false;
          const footer = document.querySelector('.footer');
          const footerHeight = footer ? footer.offsetHeight : 0;
          
          const updateProgress = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight - footerHeight;
            const scrollPercent = (scrollTop / docHeight) * 100;
            
            progressBar.style.width = Math.min(scrollPercent, 100) + '%';
            ticking = false;
          };
          
          window.addEventListener('scroll', () => {
            if (!ticking) {
              window.requestAnimationFrame(updateProgress);
              ticking = true;
            }
          });
          
          // Initial calculation
          updateProgress();
        } else {
          // Desktop: Use ScrollTrigger for smooth scrubbing
          gsap.to(progressBar, {
            width: '100%',
            ease: 'none',
            scrollTrigger: {
              trigger: document.body,
              start: 'top top',
              end: 'bottom bottom',
              scrub: true,
              markers: false,
              invalidateOnRefresh: true
            }
          });
        }
      }
    }
  }
});